import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import LanguageToggle from "@/components/LanguageToggle";
import Logo from "@/components/Logo";
import {
  ArrowRight, FileDown, MapPin, CloudRain, Sun, Thermometer, Snowflake, Mountain, Sprout,
  Lock, Sparkles,
} from "lucide-react";
import MapView from "@/components/MapView";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, LineChart, Line, CartesianGrid,
} from "recharts";

export default function PublicShare() {
  const { token } = useParams();
  const { t } = useI18n();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/sites/${token}`);
        setData(data);
      } catch (e) {
        setError(e.response?.data?.detail || "Link nicht gefunden");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const downloadPdf = () => {
    window.location.assign(`${API}/public/sites/${token}/pdf`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center text-[#5C6B61]">
        {t("common.loading")}
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center p-4">
        <div className="max-w-md text-center" data-testid="share-error">
          <Lock size={36} className="mx-auto text-[#C05A44] mb-3" />
          <h1 className="font-display font-bold text-2xl text-[#1A2F24] mb-2">Link nicht verfügbar</h1>
          <p className="text-[#5C6B61] mb-6">{error}</p>
          <Button onClick={() => nav("/")} className="bg-[#1A2F24] hover:bg-[#2E3B32] text-white">
            Zur Startseite
          </Button>
        </div>
      </div>
    );
  }

  const { site, owner, report } = data;
  const climate = report.climate || {};
  const hz = report.hardiness_zone || {};
  const soil = report.soil || {};
  const monthNames = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];
  const chartData = (climate.monthly || []).map((m) => ({
    month: monthNames[m.month - 1],
    precipitation: m.precipitation_mm,
    temperature: m.temperature_c,
  }));

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="public-share-page">
      {/* Header */}
      <header className="border-b border-[#E5E2D9] bg-white sticky top-0 z-20">
        <div className="container mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-3">
            <LanguageToggle />
            <Button
              data-testid="share-cta-register"
              onClick={() => nav("/register")}
              className="bg-[#C05A44] hover:bg-[#A64B38] text-white hidden sm:inline-flex"
            >
              Eigenes Konto erstellen
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 md:px-8 py-10 md:py-14">
        {/* Title block */}
        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.2em] text-[#C05A44] font-medium mb-3">
            Geteilte Standortdaten
          </div>
          <h1 className="font-display font-bold text-3xl md:text-5xl tracking-tighter text-[#1A2F24] mb-2" data-testid="share-site-title">
            {site.name}
          </h1>
          <p className="text-[#5C6B61] text-base md:text-lg max-w-3xl">{site.address}</p>
          {owner.company && (
            <p className="text-sm text-[#5C6B61] mt-3">
              Erstellt von <span className="font-medium text-[#1A2F24]">{owner.company}</span>
              {owner.name && <span> — {owner.name}</span>}
            </p>
          )}
        </div>

        {/* Download PDF */}
        <div className="mb-10">
          <Button
            data-testid="share-download-pdf"
            onClick={downloadPdf}
            variant="outline"
            className="border-[#1A2F24] text-[#1A2F24] hover:bg-[#F1EFE7]"
          >
            <FileDown size={16} className="mr-2" /> PDF-Bericht herunterladen
          </Button>
        </div>

        {/* Overview + Map */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="bg-white border border-[#E5E2D9] rounded-xl p-6">
            <h2 className="font-display font-semibold text-xl text-[#1A2F24] mb-4 flex items-center gap-2">
              <MapPin size={20} className="text-[#5C6B61]" /> Übersicht
            </h2>
            <dl className="space-y-3 text-sm">
              <Field label="Koordinaten" value={`${report.coordinates.latitude.toFixed(5)}, ${report.coordinates.longitude.toFixed(5)}`} />
              <Field label="Land" value={report.address?.country || "—"} />
              <Field label="Region" value={report.address?.state || "—"} />
              <Field label="Höhe ü. NN" value={report.elevation_m != null ? `${Math.round(report.elevation_m)} m` : "—"} icon={Mountain} />
            </dl>
          </div>
          <MapView lat={report.coordinates.latitude} lng={report.coordinates.longitude} height={340} />
        </div>

        {/* Hardiness zone hero */}
        <div className="bg-[#1A2F24] text-white rounded-xl p-6 md:p-8 mb-6 relative overflow-hidden">
          <div className="absolute inset-0 grain-bg opacity-20" />
          <div className="relative grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-white/60 mb-2">Winterhärtezone</div>
              <div className="font-display font-bold text-6xl md:text-7xl tracking-tighter">
                {hz.zone || "—"}
              </div>
            </div>
            <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <DarkField label="USDA-Klasse" value={hz.label || "—"} />
              <DarkField label="Temperaturbereich" value={hz.range_c || "—"} />
              <DarkField
                label="Planungswert (kälteste Winter)"
                value={hz.min_temp_c != null ? `${hz.min_temp_c} °C` : "—"}
              />
              {hz.usda_mean_annual_minimum_c != null && (
                <DarkField
                  label="USDA-Mittel (alle Jahre)"
                  value={`${hz.usda_mean_annual_minimum_c} °C`}
                />
              )}
            </div>
          </div>
        </div>

        {/* Climate KPIs */}
        <div className="mb-6">
          <h2 className="font-display font-semibold text-xl text-[#1A2F24] mb-1">Klimadaten</h2>
          <div className="text-xs text-[#5C6B61] mb-4">{climate.period || ""}</div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Kpi icon={Thermometer} label="Ø Temperatur" value={climate.annual_mean_temp_c != null ? `${climate.annual_mean_temp_c} °C` : "—"} />
            <Kpi icon={CloudRain} label="Niederschlag/Jahr" value={climate.annual_precipitation_mm != null ? `${climate.annual_precipitation_mm} mm` : "—"} />
            <Kpi icon={Sun} label="Sonnenstunden/Jahr" value={climate.annual_sunshine_hours != null ? `${climate.annual_sunshine_hours} h` : "—"} />
            <Kpi icon={Snowflake} label="Frosttage/Jahr" value={climate.frost_days_per_year != null ? `${climate.frost_days_per_year}` : "—"} />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white border border-[#E5E2D9] rounded-xl p-5">
              <div className="text-sm font-medium text-[#1A2F24] mb-3">Niederschlag (mm/Monat)</div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E2D9" />
                  <XAxis dataKey="month" stroke="#5C6B61" fontSize={11} />
                  <YAxis stroke="#5C6B61" fontSize={11} />
                  <Tooltip contentStyle={{ background: "#1A2F24", border: "none", borderRadius: 6, color: "#fff", fontSize: 12 }} />
                  <Bar dataKey="precipitation" fill="#6B9E9E" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white border border-[#E5E2D9] rounded-xl p-5">
              <div className="text-sm font-medium text-[#1A2F24] mb-3">Ø Temperatur (°C/Monat)</div>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E2D9" />
                  <XAxis dataKey="month" stroke="#5C6B61" fontSize={11} />
                  <YAxis stroke="#5C6B61" fontSize={11} />
                  <Tooltip contentStyle={{ background: "#1A2F24", border: "none", borderRadius: 6, color: "#fff", fontSize: 12 }} />
                  <Line type="monotone" dataKey="temperature" stroke="#C05A44" strokeWidth={2.5} dot={{ r: 3, fill: "#C05A44" }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Soil (light) */}
        <div className="bg-white border border-[#E5E2D9] rounded-xl p-6 mb-10">
          <h2 className="font-display font-semibold text-xl text-[#1A2F24] mb-4 flex items-center gap-2">
            <Sprout size={20} className="text-[#5C6B61]" /> Bodendaten
          </h2>
          {soil.available ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Kpi icon={Sprout} label="Bodentyp" value={soil.texture_class || "—"} />
              <Kpi label="pH-Wert" value={soil.ph ?? "—"} />
              <div className="bg-[#F1EFE7] rounded-lg p-4 border border-dashed border-[#C05A44] relative">
                <div className="text-[10px] uppercase tracking-wide text-[#5C6B61] mb-1 flex items-center gap-1">
                  <Lock size={11} /> Premium
                </div>
                <div className="text-sm font-medium text-[#1A2F24] mb-2">Detaillierte Bodenanalyse</div>
                <p className="text-xs text-[#5C6B61] mb-2">Sand/Schluff/Ton-Anteile, organ. Kohlenstoff, Lagerungsdichte</p>
                <Link to="/register" className="text-xs text-[#C05A44] font-medium hover:underline">Konto erstellen →</Link>
              </div>
            </div>
          ) : (
            <>
              <div className="text-sm text-[#5C6B61]">Bodendaten nicht verfügbar für diesen Standort.</div>
              {soil.association && (
                <div className="mt-3 rounded-lg bg-[#F1EFE7] border border-[#E5E2D9] px-4 py-3">
                  <div className="text-[10px] uppercase tracking-wide text-[#5C6B61] mb-1">
                    Bodenkundliche Einheit
                  </div>
                  <div className="font-display font-semibold text-[#1A2F24]">
                    {soil.association.name}
                    {soil.association.unit && (
                      <span className="font-normal text-[#5C6B61]"> · Einheit {soil.association.unit}</span>
                    )}
                  </div>
                  <p className="text-xs text-[#5C6B61] mt-2">
                    Übersichtskarte ohne Messwerte. {soil.association.attribution}
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* LEAD-GEN CTA — the viral hook */}
        <div className="bg-[#1A2F24] text-white rounded-xl p-8 md:p-12 relative overflow-hidden">
          <div className="absolute inset-0 grain-bg opacity-25" />
          <div className="relative grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/20 rounded-full px-3 py-1 text-xs uppercase tracking-wider mb-5">
                <Sparkles size={14} /> Für GaLaBauer
              </div>
              <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tighter mb-4 leading-[1.1]">
                Eigene Standortdaten<br />in unter 60 Sekunden.
              </h2>
              <p className="text-white/75 mb-7 max-w-md">
                Geocoding, Klimadaten, Winterhärtezonen und Bodendaten für jede Adresse in der DACH-Region. Inklusive PDF-Export mit Ihrem Logo.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button
                  data-testid="share-cta-register-bottom"
                  onClick={() => nav("/register")}
                  size="lg"
                  className="bg-[#C05A44] hover:bg-[#A64B38] text-white h-12 px-6"
                >
                  Mit Messe-Code starten <ArrowRight size={18} className="ml-1" />
                </Button>
              </div>
              <p className="text-sm text-white/60 mt-5 max-w-md" data-testid="share-fair-note">
                Exklusiver Messe-Test: Die Registrierung ist aktuell nur mit dem Code von der Messe möglich — damit schalten Sie das Paket Free frei.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: "Klimadaten", value: "10 Jahre" },
                { label: "Winterhärte", value: "USDA" },
                { label: "Boden", value: "ISRIC" },
                { label: "PDF-Export", value: "Inklusive" },
              ].map((s, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-lg p-4">
                  <div className="text-[10px] uppercase tracking-wide text-white/60">{s.label}</div>
                  <div className="text-xl font-display font-bold text-white mt-1">{s.value}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      <footer className="border-t border-[#E5E2D9] py-8 bg-[#F1EFE7] mt-16">
        <div className="container mx-auto px-4 md:px-8 flex flex-wrap items-center justify-between gap-3">
          <Logo small />
          <div className="text-xs text-[#5C6B61]">
            Geteilter Standortbericht · Powered by BaugrundCheck.de
          </div>
          <div className="flex gap-4 text-xs text-[#5C6B61]">
            <Link to="/impressum" className="hover:text-[#1A2F24]" data-testid="share-impressum-link">Impressum</Link>
            <Link to="/datenschutz" className="hover:text-[#1A2F24]" data-testid="share-datenschutz-link">Datenschutz</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Field({ label, value, icon: Icon }) {
  return (
    <div className="flex gap-3 text-sm">
      <dt className="w-28 text-[#5C6B61] flex items-center gap-1.5 shrink-0">{Icon && <Icon size={14} />} {label}</dt>
      <dd className="text-[#1A2F24] font-medium">{value}</dd>
    </div>
  );
}
function DarkField({ label, value }) {
  return (
    <div className="bg-white/5 rounded-lg p-3 border border-white/10">
      <div className="text-[10px] uppercase tracking-wide text-white/60">{label}</div>
      <div className="text-sm font-medium text-white mt-1">{value}</div>
    </div>
  );
}
function Kpi({ icon: Icon, label, value }) {
  return (
    <div className="bg-[#F1EFE7] rounded-lg p-4 border border-[#E5E2D9]">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-wide text-[#5C6B61]">{label}</span>
        {Icon ? <Icon size={14} className="text-[#5C6B61]" /> : null}
      </div>
      <div className="text-lg font-display font-bold text-[#1A2F24] leading-tight">{value}</div>
    </div>
  );
}
